import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import api from "../api";
import { useLanguage } from "../context/LanguageContext";
import PageContent from "../sections/PageContent";
import { useSiteTheme } from "../theme/ThemeContext";
import { radius } from "../styles/tokens";
import SEO from "../components/SEO";

interface SiteContent {
  about_title_en: string;
  about_title_ro: string;
  about_content_en: string;
  about_content_ro: string;
}

function Story() {
  const { language } = useLanguage();

  const [content, setContent] = useState<SiteContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { palette } = useSiteTheme();

  useEffect(() => {
    const fetchContent = async (): Promise<void> => {
      try {
        setLoading(true);

        const response = await api.get<SiteContent>("/content");

        setContent(response.data);
        setError(null);
      } catch (error) {
        console.error("Error fetching About content:", error);
        setError("Failed to load About content");
      } finally {
        setLoading(false);
      }
    };

    void fetchContent();
  }, []);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !content) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error">
          {error ?? "About content is unavailable"}
        </Alert>
      </Container>
    );
  }

  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        md: "minmax(0, 1.2fr) minmax(280px, 0.8fr)",
      },
      gap: { xs: 4, md: 6 },
      alignItems: "start",
    }}
  >
    <Box>
      const title = language === 'ro' ? content.about_title_ro :
      content.about_title_en; const body = language === 'ro' ?
      content.about_content_ro : content.about_content_en;
    </Box>

    <Box
      component="img"
      src="images/story/studio.png"
      alt="The artist working in her garden studio"
      sx={{
        width: "100%",
        display: "block",
        borderRadius: radius.surface,
        objectFit: "cover",
        maxHeight: 560,
      }}
    />
  </Box>;

  const title =
    language === "ro" ? content.about_title_ro : content.about_title_en;

  const body =
    language === "ro" ? content.about_content_ro : content.about_content_en;

  return (
    <>
      <SEO
        title="About the Artist"
        description="Learn about the artist behind Byzantine Art Studio and the traditional techniques used to create hand-painted Byzantine iconography."
        url="/about"
        keywords="Byzantine Art Studio, icon painter, Byzantine iconography, Orthodox icons, hand-painted icons"
      />

      <PageContent bg={palette.hero}>
        <Box
          sx={{
            bgcolor: palette.featured,
            p: 4,
            borderRadius: radius.surface,
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom>
            {title}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0, 1.2fr) minmax(280px, 0.8fr)",
            },
            gap: { xs: 4, md: 6 },
            alignItems: "start",
          }}
        >
          <Box>
            {body.split("\n").map((paragraph, index) => (
              <Typography key={index} variant="body1" paragraph>
                {paragraph}
              </Typography>
            ))}
          </Box>

          <Box
            component="img"
            src="/images/story/studio.webp"
            alt="The garden studio where the icons are painted"
            sx={{
              width: "100%",
              display: "block",
              borderRadius: radius.surface,
              objectFit: "cover",
              maxHeight: 560,
            }}
          />
        </Box>
      </PageContent>
    </>
  );
}

export default Story;
